import React, { useState, useEffect } from 'react';
import { Package, ScanLine, TrendingUp, ShoppingCart, Activity, Clock, BarChart2, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const tooltipStyle = { background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)', borderRadius: '10px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#E2E8F0' };

function KPICard({ icon: Icon, label, value, sub, color, loading, live }) {
  return (
    <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)', backdropFilter: 'blur(10px)' }}>
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-5" style={{ background: `radial-gradient(circle, ${color}, transparent 70%)`, transform: 'translate(30%, -30%)' }} />
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
          <Icon size={18} style={{ color }} />
        </div>
        {live && (
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>Live</span>
          </div>
        )}
      </div>
      {loading ? (
        <div className="h-8 w-24 rounded-lg animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />
      ) : (
        <p className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>{value}</p>
      )}
      <p className="text-xs font-medium" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{label}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color, fontFamily: 'DM Sans, sans-serif', opacity: 0.8 }}>{sub}</p>}
    </div>
  );
}

const ACTION_LABELS = { viewed: 'Viewed', purchased: 'Purchased', added_to_budget: 'Added to Budget' };
const ACTION_COLORS = { viewed: '#00E5FF', purchased: '#10B981', added_to_budget: '#7B2FBE' };

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function DashboardOverview({ partnerId }) {
  const [stats, setStats] = useState({ totalProducts: 0, scansToday: 0, scansMonth: 0, interestRate: 0, conversionRate: 0 });
  const [recentScans, setRecentScans] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveCount, setLiveCount] = useState(0);

  const fetchStats = async () => {
    if (!partnerId) return;
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1)?.toISOString();
      const weekStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString();

      const [productsRes, scansRes, recentRes, weekScansRes] = await Promise.all([
        supabase?.from('retail_products')?.select('*', { count: 'exact', head: true })?.eq('partner_id', partnerId),
        supabase?.from('product_scans')?.select('action_taken, scanned_at')?.eq('partner_id', partnerId),
        supabase?.from('product_scans')
          ?.select('id, action_taken, scanned_at, retail_products(product_name, category)')
          ?.eq('partner_id', partnerId)
          ?.order('scanned_at', { ascending: false })
          ?.limit(10),
        supabase?.from('product_scans')
          ?.select('retail_products(id, product_name), action_taken')
          ?.eq('partner_id', partnerId)
          ?.gte('scanned_at', weekStart),
      ]);

      const allScans = scansRes?.data || [];
      const todayScans = allScans?.filter(s => s?.scanned_at?.startsWith(today));
      const monthScans = allScans?.filter(s => s?.scanned_at >= monthStart);
      const purchases = allScans?.filter(s => s?.action_taken === 'purchased');
      const convRate = allScans?.length > 0 ? Math.round((purchases?.length / allScans?.length) * 100) : 0;

      // Top 5 products this week
      const prodMap = {};
      (weekScansRes?.data || [])?.forEach(s => {
        const pid = s?.retail_products?.id;
        if (!pid) return;
        if (!prodMap?.[pid]) prodMap[pid] = { name: s?.retail_products?.product_name || 'Unknown', scans: 0 };
        prodMap[pid].scans++;
      });
      const top5 = Object.values(prodMap)?.sort((a, b) => b?.scans - a?.scans)?.slice(0, 5)?.map(p => ({
        name: p?.name?.length > 18 ? p?.name?.substring(0, 18) + '…' : p?.name,
        scans: p?.scans,
      }));

      setStats({
        totalProducts: productsRes?.count || 0,
        scansToday: todayScans?.length,
        scansMonth: monthScans?.length,
        interestRate: convRate,
        conversionRate: convRate,
      });
      setLiveCount(todayScans?.length);
      setRecentScans(recentRes?.data || []);
      setTopProducts(top5);
    } catch (e) {
      console.error('Dashboard stats error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const channel = supabase?.channel(`partner_scans_live_${partnerId}`)
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'product_scans', filter: `partner_id=eq.${partnerId}` },
        (payload) => {
          setLiveCount(c => c + 1);
          setRecentScans(prev => [payload?.new, ...prev]?.slice(0, 10));
        }
      )?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [partnerId]);

  const kpis = [
    { icon: Package, label: 'Total Products Listed', value: stats?.totalProducts, color: '#00E5FF' },
    { icon: ScanLine, label: 'Total Scans Today', value: liveCount, sub: 'Updates in real-time', color: '#7B2FBE', live: true },
    { icon: Activity, label: 'Scans This Month', value: stats?.scansMonth, color: '#10B981' },
    { icon: TrendingUp, label: 'Conversion Rate', value: `${stats?.conversionRate}%`, sub: 'Scans → purchases', color: '#F59E0B' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Dashboard Overview</h2>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Real-time performance metrics for your product catalog</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis?.map((k, i) => <KPICard key={i} {...k} loading={loading} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent Activity */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} style={{ color: '#00E5FF' }} />
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Recent Activity</h3>
            <span className="ml-auto flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs" style={{ color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>Live</span>
            </span>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)]?.map((_, i) => (
                <div key={i} className="h-12 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} />
              ))}
            </div>
          ) : recentScans?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Zap size={24} style={{ color: '#1E3A5F' }} />
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No scan activity yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {recentScans?.map((scan, i) => {
                const action = scan?.action_taken || 'viewed';
                const color = ACTION_COLORS?.[action] || '#00E5FF';
                return (
                  <div key={scan?.id || i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                      <ShoppingCart size={13} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        {scan?.retail_products?.product_name || 'Product'}
                      </p>
                      <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>
                        {scan?.retail_products?.category || ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color, fontFamily: 'DM Sans, sans-serif' }}>
                        {ACTION_LABELS?.[action] || action}
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>
                        {timeAgo(scan?.scanned_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Products Chart */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 size={16} style={{ color: '#7B2FBE' }} />
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Top 5 Products This Week</h3>
          </div>
          {loading ? (
            <div className="h-52 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.03)' }} />
          ) : topProducts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-52 gap-2">
              <BarChart2 size={24} style={{ color: '#1E3A5F' }} />
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No scan data this week</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={topProducts} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10, fontFamily: 'DM Sans' }} />
                <YAxis tick={{ fill: '#64748B', fontSize: 10, fontFamily: 'DM Sans' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="scans" fill="url(#barGrad)" radius={[4, 4, 0, 0]} />
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#7B2FBE" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
